import { cn } from '@/lib/utils/cn'
import type { ReservationBoardCourtView, ReservationBoardSlotView } from '@/lib/venue/reservationTypes'
import { boardCellKind, formatHoldRemaining } from '@/lib/venue/reservationViewModel'

import { SWATCH_FILL_CLASS } from './ReservationLegend'

function slotTitle(slot: ReservationBoardSlotView): string | undefined {
  if (slot.state === 'BLOCKED') return slot.closureTitle ?? slot.closureReason ?? undefined
  return undefined
}

function slotCellText(slot: ReservationBoardSlotView): string {
  if (slot.state === 'BLOCKED') return slot.closureTitle ?? slot.closureTypeLabel ?? '차단'
  if (slot.state === 'RESERVED') {
    if (slot.bookerPurged) return '(파기됨)'
    return slot.bookerName ?? '-'
  }
  return ''
}

function SlotBadge({ slot }: { slot: ReservationBoardSlotView }) {
  if (slot.reservationStatus === 'HELD') {
    return (
      <p className="mt-0.5 truncate text-[10px] text-slateish-600">
        결제 진행 중 · 잔여 {formatHoldRemaining(slot.holdRemainingSeconds)}
      </p>
    )
  }
  if (slot.reservationStatus === 'PENDING') {
    return <p className="mt-0.5 truncate text-[10px] text-slateish-600">결제 성공 · 예약 확정 재시도 중</p>
  }
  return null
}

function BoardCell({
  slot,
  onSelectReservation,
}: {
  slot: ReservationBoardSlotView
  onSelectReservation: (reservationId: number) => void
}) {
  const kind = boardCellKind(slot)

  if (!kind) {
    // OPEN — 빈칸.
    return <div className="min-h-[44px] px-2 py-1.5" />
  }

  const clickable = slot.state === 'RESERVED' && slot.reservationId !== null

  return (
    <div
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      title={slotTitle(slot)}
      onClick={() => {
        if (clickable && slot.reservationId !== null) onSelectReservation(slot.reservationId)
      }}
      onKeyDown={(event) => {
        if (!clickable || slot.reservationId === null) return
        if (event.key === 'Enter' || event.key === ' ') onSelectReservation(slot.reservationId)
      }}
      className={cn(
        'min-h-[44px] px-2 py-1.5 text-xs',
        SWATCH_FILL_CLASS[
          kind === 'APP' ? 'brand' : kind === 'MANUAL' ? 'warning' : kind === 'LESSON' ? 'success' : 'neutralDark'
        ],
        clickable && 'cursor-pointer'
      )}
    >
      <p className="truncate font-medium">{slotCellText(slot)}</p>
      <SlotBadge slot={slot} />
    </div>
  )
}

/**
 * 코트×시간 슬롯 그리드(설계 §2-4(1)) — Figma `예약 캘린더`(13989-43050) 실물과 1:1.
 * 🚨 막힌 칸도 그린다(빈 칸이면 관리자가 "왜 이 시간이 없지"를 알 수 없다). 빈 배열인 코트는
 * 그 요일 미운영·정지 상태로 안내한다.
 */
export function ReservationBoardTable({
  courts,
  onSelectReservation,
}: {
  courts: ReservationBoardCourtView[]
  onSelectReservation: (reservationId: number) => void
}) {
  const operatingCourts = courts.filter((court) => court.slots.length > 0)
  const closedCourts = courts.filter((court) => court.slots.length === 0)

  const timeRows = Array.from(
    new Set(operatingCourts.flatMap((court) => court.slots.map((slot) => slot.startTime)))
  ).sort()

  return (
    <div className="space-y-3">
      {closedCourts.length > 0 ? (
        <p className="rounded-lg bg-slateish-50 px-3 py-2 text-xs text-slateish-600">
          {closedCourts.map((court) => court.courtName).join(', ')}: 이 코트는 이 날짜에 예약을 받지 않습니다.
        </p>
      ) : null}

      {operatingCourts.length === 0 ? null : (
        <div className="overflow-x-auto rounded-xl border border-slateish-200">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-slateish-200 bg-slateish-50">
                <th className="w-16 px-2 py-2 text-left text-xs font-medium text-slateish-600">시간</th>
                {operatingCourts.map((court) => (
                  <th key={court.courtId} className="px-2 py-2 text-left text-xs font-medium text-slateish-600">
                    {court.courtName}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {timeRows.map((time) => (
                <tr key={time} className="border-b border-slateish-200 last:border-b-0">
                  <td className="px-2 py-1.5 align-top text-xs text-slateish-600">{time}</td>
                  {operatingCourts.map((court) => {
                    const slot = court.slots.find((entry) => entry.startTime === time)
                    return (
                      <td key={court.courtId} className="align-top">
                        {slot ? (
                          <BoardCell slot={slot} onSelectReservation={onSelectReservation} />
                        ) : (
                          <div className="min-h-[44px] px-2 py-1.5" />
                        )}
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
